/**
 * Live Agent (`simulation` view) — reduced to orchestration + state per
 * ADR-0159 §6 module split. The picker roster, per-protocol journeys,
 * live-fetch helpers, and the component leaves live in sibling files;
 * this file's responsibility is:
 *
 *   - Wire the active site → discovery URLs (agent card, ARD catalog,
 *     MCP endpoint).
 *   - Fire the LIVE discovery fetches on Run + the LIVE MCP tools/list
 *     POST (Claude path only, gated by the one-shot `useRef`).
 *   - Fetch the merchant's synced catalog for the transactional cart
 *     illustration.
 *   - Resolve the current agent's tier via `useAgentRegistry` and
 *     thread it into the picker + mandate signing card.
 *   - Render the timeline + agent + endpoint + identity-evidence
 *     panels; the shipped/dogfood post-identity banner for the
 *     blocked path stays inline (test-verified).
 *
 * The public export is unchanged: `export function Simulation()`. The
 * view-router import path (`../views/simulation/Simulation.js`) still
 * resolves to this module.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Info, ShieldAlert } from 'lucide-react';

import { useSites } from '../../app/providers.js';
import { getCatalog } from '../../api/catalog.js';
import { useOptionalApi } from '../../api/api-context.js';
import { useOptionalHttp } from '../../api/http-context.js';
import {
  buildAgentCardUrl,
  buildCatalogUrl,
  buildMcpUrl,
  isCatalogSentinelUrl,
  isMcpSentinelUrl,
} from '../../lib/install-snippet.js';
import { useC } from '../../lib/theme.js';
import { useIsMobile } from '../../lib/use-is-mobile.js';
import { Card, NumLabel } from '../../components/atoms/index.js';

import { AgentColumn } from './components/AgentColumn.js';
import { EndpointColumn } from './components/EndpointColumn.js';
import { IdentityEvidencePanel } from './components/IdentityEvidencePanel.js';
import { ObservedActivityPanel } from './components/ObservedActivityPanel.js';
import { PhaseTimeline } from './components/PhaseTimeline.js';
import { SimulationControls } from './components/SimulationControls.js';
import { UnifiedPhaseList } from './components/UnifiedPhaseList.js';
import { useAgentActivity } from './hooks/useAgentActivity.js';
import { useAgentRegistry } from './hooks/useAgentRegistry.js';
import { useViewMode } from './hooks/useViewMode.js';
import { buildJourney } from './journeys/index.js';
import {
  pickCartProduct,
  SAMPLE_FALLBACK,
} from './journeys/discovery.js';
import type { CartProduct, LiveProducts, Phase } from './journeys/types.js';
import { gatingReasonToNextStep } from '../../lib/skill-status-copy.js';
import {
  cardSkillsFact,
  cartFacts,
  checkoutNotLiveFact,
  notOnCardFact,
  parseToolNames,
  requesterFacts,
  summarizeProducts,
  type Fact,
  type SiteSkillsSnapshot,
} from './lib/facts.js';
import { fetchSurface, type LiveData } from './live/fetch-surface.js';
import { callToolsList } from './live/mcp-tools-list.js';
import { findAgent } from './roster.js';

const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export function Simulation(): JSX.Element {
  const C = useC();
  const { activeSite } = useSites();
  const http = useOptionalHttp();
  const api = useOptionalApi();
  const { tierFor, failed: registryFailed } = useAgentRegistry();
  const isMobile = useIsMobile();
  const { mode, setMode } = useViewMode();
  const [agentId, setAgentId] = useState('chatgpt');
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(-1);
  const [live, setLive] = useState<LiveData>({});
  const [liveProducts, setLiveProducts] = useState<LiveProducts>({ kind: 'unknown' });
  // The site's skill toggles (full enabled/disabled partition) — fetched
  // once per Run (same cadence as the catalog). Powers the Discovery
  // card's toggled-off list (EVERY skill accounted for, by name) and the
  // transactional phases' place_order note. `null` = unknown (sample
  // mode, fetch pending, or the API bag has no skills surface) — no
  // gating, no notes. Founder feedback 2026-07-05.
  const [siteSkills, setSiteSkills] = useState<SiteSkillsSnapshot | null>(null);

  // The card + catalog + MCP endpoint accept the tenant-readable
  // `site_key_hash` directly at the edge (ADR-0055). A missing/invalid key
  // yields a sentinel URL → `installed === false` → the demo runs in
  // illustrative sample mode.
  const siteKeyHash = activeSite?.site_key_hash ?? '';
  const cardUrl = buildAgentCardUrl(siteKeyHash);
  const catalogUrl = buildCatalogUrl(siteKeyHash);
  const mcpUrl = buildMcpUrl(siteKeyHash);
  const installed = !isCatalogSentinelUrl(catalogUrl);
  const mcpAvailable = !isMcpSentinelUrl(mcpUrl);

  const agent = findAgent(agentId);
  const tier = tierFor(agentId);

  // ADR-0161 — OBSERVED tier fetch. Hook is safe when the panel is
  // hidden: it short-circuits to `idle` and issues no request when
  // `edgeAgentIds` is empty. `siteId` is nulled when the site is not
  // installed so the "no fetch when not installed" invariant (ADR-0161
  // §4/§5) holds even for a site row with a malformed key hash — a
  // skipped fetch never spends rate-limit budget. The `range` is
  // pinned to `'7d'` for the Live Agent view (ADR-0161 §4).
  const activity = useAgentActivity({
    siteId: installed ? (activeSite?.site_id ?? null) : null,
    edgeAgentIds: agent.edgeAgentIds,
    range: '7d',
  });
  const showObservedPanel =
    installed
    && agent.edgeAgentIds.length > 0
    && agent.protocol !== 'custom'
    && agent.protocol !== 'blocked';

  // Cart product — real when the store has synced products; sample fallback
  // otherwise. See `journeys/discovery.ts::pickCartProduct` for the
  // eligibility ranking.
  const cart: CartProduct = useMemo(() => {
    if (liveProducts.kind === 'resolved') {
      return pickCartProduct(liveProducts.products) ?? SAMPLE_FALLBACK;
    }
    return SAMPLE_FALLBACK;
  }, [liveProducts]);

  const phases = useMemo(
    () =>
      buildJourney({
        agent,
        live,
        installed,
        cardUrl,
        catalogUrl,
        mcpUrl,
        liveProducts,
        cart,
      }),
    [agent, live, installed, cardUrl, catalogUrl, mcpUrl, liveProducts, cart],
  );

  // ADR-0163 §1 slot ③ — derived facts per phase. Typed sources only
  // (`live.mcp`, `liveProducts`, `cart`, the endpoint's documented UA);
  // a phase kind with no derivable fact renders zero fact lines.
  const factsFor = useCallback(
    (p: Phase): readonly Fact[] => {
      // Verbose requester (caption + full UA) on Discovery only — the
      // phase where the documented signature IS the story.
      const facts: Fact[] = [...requesterFacts(p.endpoint, { verbose: p.id === 'discover' })];
      if (p.id === 'discover') {
        const f = cardSkillsFact(live.card);
        if (f !== null) facts.push(f);
        // Reconcile Settings with the card: every skill NOT on the card
        // is named once with its reason (toggled off / coming soon /
        // the merchant-actionable next step) — including skills no
        // journey phase exercises (request_refund, recommend, …).
        const off = notOnCardFact(siteSkills);
        if (off !== null) facts.push(off);
      } else if (p.id === 'mcp-tools-list') {
        const f = parseToolNames(live.mcp);
        if (f !== null) facts.push(f);
      } else if (p.id === 'intent' || p.id === 'cart' || p.id === 'pay' || p.id === 'mcp-tool-call') {
        // place_order not LIVE on the card (toggled off, awaiting a
        // buyable product, tier-gated) → the amber honesty note REPLACES
        // the cart fact, and renders ONCE — on the first transactional
        // phase only (repeating it on every checkout step read as
        // nagging; 2026-07-06 consolidation).
        const disabled = checkoutNotLiveFact(siteSkills);
        if (disabled !== null) {
          if (p.id === 'intent' || p.id === 'mcp-tool-call') facts.push(disabled);
        } else {
          const f = cartFacts(cart);
          if (f !== null) facts.push(f);
        }
      } else if (p.id === 'browse') {
        const f = summarizeProducts(liveProducts);
        if (f !== null) facts.push(f);
      }
      return facts;
    },
    [live.card, live.mcp, liveProducts, cart, siteSkills],
  );

  // One-shot MCP `tools/list` gate (ADR-0159 §5). Flag is set BEFORE the
  // fetch is dispatched, so a fast double-tap of Run cannot double-fire.
  // Cleared inside `reset()` and on `agentId` change.
  const mcpCalledForThisRun = useRef(false);

  const reset = (): void => {
    setRunning(false);
    setStep(-1);
    setLive({});
    setLiveProducts({ kind: 'unknown' });
    setSiteSkills(null);
    mcpCalledForThisRun.current = false;
  };
  useEffect(() => {
    reset();
    // Clear the one-shot flag when the picked agent changes, so a Claude
    // → Claude re-selection after a Grok interlude re-arms the MCP call.
    mcpCalledForThisRun.current = false;
  }, [agentId]);

  useEffect(() => {
    if (!running) return;
    if (step >= phases.length - 1) {
      setRunning(false);
      return;
    }
    const timer = setTimeout(() => setStep(step + 1), 900);
    return () => clearTimeout(timer);
  }, [running, step, phases.length]);

  const start = (): void => {
    setStep(0);
    setRunning(true);
    setLive({});
    setLiveProducts({ kind: 'unknown' });
    setSiteSkills(null);
    // Fire the REAL public discovery fetches in parallel. They resolve
    // independently of the step animation; when they land, `live` updates
    // and the discover/browse phases re-render with the real response.
    if (installed) {
      void fetchSurface(cardUrl).then((card) => setLive((prev) => ({ ...prev, card })));
      void fetchSurface(catalogUrl).then((catalog) =>
        setLive((prev) => ({ ...prev, catalog })),
      );
    }
    // ADR-0159 §5 — one-shot MCP `tools/list` POST, Claude protocol only.
    // Gated on `installed === true && agent.protocol === 'mcp' &&
    // mcpAvailable && !mcpCalledForThisRun`. Flag set BEFORE dispatch.
    if (
      installed
      && mcpAvailable
      && agent.protocol === 'mcp'
      && !mcpCalledForThisRun.current
    ) {
      mcpCalledForThisRun.current = true;
      void callToolsList(mcpUrl).then((mcp) => setLive((prev) => ({ ...prev, mcp })));
    }
    // Fetch the merchant's REAL synced products — used for the Browse
    // phase (count + a few example titles) + the cart / pay illustration.
    // Any non-ok result is mapped to "0 products" (sample mode). Never
    // throws.
    if (installed && http !== null && activeSite !== null) {
      setLiveProducts({ kind: 'loading' });
      void getCatalog(http, activeSite.site_id, {
        limit: 12,
        sort: 'price_minor',
        dir: 'desc',
      }).then((res) => {
        if (res.kind === 'ok') {
          setLiveProducts({ kind: 'resolved', products: res.page.products });
        } else {
          setLiveProducts({ kind: 'resolved', products: [] });
        }
      });
    }
    // Fetch the site's ENABLED skills for the honest place_order-toggled-
    // off note on transactional phases. Defensive on every axis: the API
    // bag may be absent (older test stubs), the call may reject — both
    // collapse to `null` (no note, no gating). Never throws.
    if (installed && api !== null && activeSite !== null) {
      const listForSite = api.skills?.listForSite;
      if (typeof listForSite === 'function') {
        void Promise.resolve(listForSite(activeSite.site_id))
          .then((items) => {
            // Status-first (ADR-0129 wire shape); a stub without `status`
            // derives it from the toggle so older mocks stay truthful.
            const withStatus = items.map((s) => ({
              skill_id: s.skill_id as string,
              status: s.status ?? (s.enabled ? 'live' : 'disabled'),
              next_step:
                s.gating_reason !== undefined ? gatingReasonToNextStep(s.gating_reason) : undefined,
            }));
            setSiteSkills({
              live: withStatus.filter((s) => s.status === 'live').map((s) => s.skill_id),
              not_live: withStatus
                .filter((s) => s.status !== 'live')
                .map(({ skill_id, status, next_step }) => ({
                  skill_id,
                  status,
                  ...(next_step !== undefined ? { next_step } : {}),
                })),
            });
          })
          .catch(() => setSiteSkills(null));
      }
    }
  };

  const isBlockedProtocol = agent.protocol === 'blocked';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <NumLabel n={1}>Live agent · real discovery</NumLabel>
          <h1 style={{ fontFamily: serifFont, fontSize: 'clamp(36px, 6vw, 52px)', lineHeight: 1, color: C.text, letterSpacing: -1.5 }}>
            Watch any agent <em>read your store</em>.
          </h1>
        </div>
      </div>

      <div className="p-3 mb-5 flex items-start gap-2" style={{ background: C.skyDim, border: `1px solid rgba(127,184,255,0.3)`, borderRadius: 2 }}>
        <Info size={13} color={C.skyFg} className="mt-0.5 flex-shrink-0" />
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
          {installed ? (
            <>
              <strong>Discovery is live.</strong> Steps tagged <span style={{ color: C.limeFg }}>LIVE</span> are real calls to your signed agent card and catalog. Checkout steps are illustration — no real charge.
            </>
          ) : (
            <>
              <strong>Sample data.</strong> Install Sill (Settings → Install) to see agents read your real card and catalog. Checkout steps are always illustration — no real charge.
            </>
          )}
        </div>
      </div>

      {/* MINOR 2 fix (ADR-0159 security review) — surface a small honest
          inline note if the AgentAPI list() call rejected. Otherwise a
          permanent-failure session is silent (every tier resolves to
          'unknown', but the merchant has no cue that the registry is
          unreachable). Positioned near the picker so it reads as
          picker-scoped, not global. */}
      {registryFailed && (
        <div
          className="p-2 mb-5 flex items-center gap-2"
          style={{ background: C.amberDim, border: `1px solid ${C.amberFg}55`, borderRadius: 2 }}
          role="status"
        >
          <ShieldAlert size={12} color={C.amberFg} className="flex-shrink-0" />
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.4 }}>
            registry unreachable — verification status unknown
          </span>
        </div>
      )}

      {/* ADR-0163 §4 — the single control locus, sticky on scroll. */}
      <SimulationControls
        agent={agent}
        agentId={agentId}
        onChangeAgent={setAgentId}
        dropdownDisabled={running}
        running={running}
        step={step}
        phaseCount={phases.length}
        onRun={start}
        onPause={() => setRunning(false)}
        tierFor={tierFor}
      />

      <PhaseTimeline phases={phases} step={step} />

      {showObservedPanel && (
        <ObservedActivityPanel state={activity.state} agent={agent} />
      )}

      {isMobile ? (
        <div className="mt-5">
          <UnifiedPhaseList
            agent={agent}
            phases={phases}
            step={step}
            tier={tier}
            mode={mode}
            onChangeMode={setMode}
            factsFor={factsFor}
            canReset={step >= 0}
            onReset={reset}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          <AgentColumn agent={agent} phases={phases} step={step} tier={tier} />
          <EndpointColumn
            phases={phases}
            step={step}
            mode={mode}
            onChangeMode={setMode}
            factsFor={factsFor}
            canReset={step >= 0}
            onReset={reset}
          />
        </div>
      )}

      {/* Identity evidence panel — the teaching layer merchants pay to see. */}
      <IdentityEvidencePanel agent={agent} />

      {/* Blocked-path banner — retained inline for the unverified journey
          (test-verified: "blocked at the door"). Rendered after step 2
          (identity gate) matches the ADR-0134 pattern the original test
          asserts on. */}
      {isBlockedProtocol && step >= 2 && (
        <Card className="p-5 mt-5" style={{ background: C.coralDim, border: `1px solid ${C.coralFg}` }}>
          <div className="flex items-start gap-3">
            <ShieldAlert size={18} color={C.coralFg} className="flex-shrink-0 mt-1" />
            <div>
              <div style={{ fontFamily: sansFont, fontSize: 16, color: C.text, fontWeight: 600, marginBottom: 4 }}>Unverified agent · blocked at the door.</div>
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.6 }}>
                No signed identity, not on the verified registry — Sill's identity gate stopped it. No mandate, no payment, no data exposed.
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
